import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';

import { ButtonModule, IconModule } from '@pinup-teams/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@fb/environments';

import { ChatMessageComponent } from '../chat-message/chat-message.component';
import {
  ChatMessage,
  ChatMessageReplyTypes,
  ChatMessageStatus,
  InternalMessageStatus,
} from '../../../../models';
import { MessageDatePipe } from '../../../../pipes/date.pipe';
import { SlackLinkDirective } from '../../../../directives/slack-link.directive';
import { AiCharStore } from '../../store';
import { MessageReactionComponent } from '../message-reaction/message-reaction.component';
import { TranslateInPipe } from '../../../../pipes/translate-in.pipe';
import { ParseMarkdownPipe } from '../../../../pipes/parse-markdown.pipe';
import { SafeHtmlPipe } from '../../../../pipes/safe-html.pipe';

@Component({
  standalone: true,
  selector: 'bot-chat-message',
  templateUrl: './bot-chat-message.component.html',
  styleUrl: './bot-chat-message.component.scss',
  imports: [
    ChatMessageComponent,
    ButtonModule,
    MessageDatePipe,
    SlackLinkDirective,
    NgTemplateOutlet,
    IconModule,
    TranslateModule,
    NgClass,
    MessageReactionComponent,
    TranslateInPipe,
    AsyncPipe,
    ParseMarkdownPipe,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BotChatMessageComponent {
  #aiChatStore = inject(AiCharStore);

  message = input.required<ChatMessage>();
  isFirst = input<boolean>(false);
  chatStarted = input<string>(null);

  text = computed(() => this.message().reply);
  links = computed(() => this.message().links || []);
  chatStartedAfter = computed(
    () => this.chatStarted() && this.message().createdAt < this.chatStarted(),
  );
  balances = computed(() => {
    const obj: Partial<Record<ChatMessageReplyTypes, number>> = {};

    this.message().timeOffBalances.forEach((timeOffBalance) => {
      obj[timeOffBalance.type] = timeOffBalance.value;
    });

    return obj;
  });

  displayReaction = computed(
    () =>
      this.isFirst() &&
      this.message().internalStatus === InternalMessageStatus.resolved &&
      this.message().status === ChatMessageStatus.Delivered &&
      this.message().isSuccessReply &&
      this.message().request,
  );

  internalMessageStatus = InternalMessageStatus;
  chatMessageStatus = ChatMessageStatus;
  chatMessageReplyTypes = ChatMessageReplyTypes;

  aiChatBotIcon = `${environment.shortName}-ai-chat-bot`;

  originLink = window.location.origin;

  tryAgain() {
    this.#aiChatStore.sendQuestion(this.message());
  }

  randomRephrase(dateStr: string): number {
    const date = new Date(dateStr);
    const milliseconds = date.getMilliseconds();

    return milliseconds % 10;
  }
}
